<template>
	<div>
		<!-- add edit dialog -->
		<v-dialog v-model="dialog" max-width="400" :persistent="submitLoading">
			<v-form ref="form" @submit.prevent="submit">
				<v-card>
					<v-card-title>
						{{!itemId ? $T('add') : $T('edit')}} {{$T('fiscal-year')}}
					</v-card-title>
					<v-card-text>
						<v-row class="ma-0">
							<!-- name -->
							<v-col cols="12" class="pa-2">
								<v-text-field
									v-model="name"
									:label="$T('inputs.name')"
									outlined
									dense
									hide-details
									:rules="rules.required"
								/>
							</v-col>
							
							<!-- start-date -->
							<v-col sm="6" cols="12" class="pa-2">
								<bee-date-input
									v-model="startDate"
									:label="$T('inputs.start-date')"
									input-format-order="DD-MM-YYYY"
									dense
									hide-details
									changeable-with-arrows
									:rules="rules.required"
								/>
							</v-col>
							
							<!-- end-date -->
							<v-col sm="6" cols="12" class="pa-2">
								<bee-date-input
									v-model="endDate"
									:label="$T('inputs.end-date')"
									input-format-order="DD-MM-YYYY"
									dense
									hide-details
									changeable-with-arrows
									:rules="rules.required"
								/>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer/>
						<v-btn type="submit"  color="primary" :loading="submitLoading">
							{{$T('save')}}
						</v-btn>
						<v-btn @click="dialog = false" :disabled="submitLoading">
							{{$T('close')}}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- change state dialog -->
		<v-dialog v-model="changeStateDialog" max-width="400" :persistent="changeStateLoading">
			<v-form ref="form" @submit.prevent="changeActiveState">
				<v-card>
					<v-card-title>
						{{$T('change-active-fiscal-year')}}
					</v-card-title>
					<v-card-text>
						{{$T('confirm-changing-active-fiscal-year')}}
					</v-card-text>
					<v-card-actions>
						<v-spacer/>
						<v-btn type="submit" color="primary" :loading="changeStateLoading">
							{{$T('save')}}
						</v-btn>
						<v-btn @click="changeStateDialog = false" :disabled="changeStateLoading">
							{{$T('close')}}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- delete dialog -->
		<v-delete-dialog
			v-model="deleteDialog"
			:ids="deleteItemId"
			moduleType="fiscalYears"
			@on-delete="fetchFiscalYears"
		>
			{{$T('confirm-delete-fiscal-year')}}
		</v-delete-dialog>

		<!-- add -->
		<v-row v-if="permissions.fiscalYears.isReadWrite" justify="end" class="mb-0">
			<v-col cols="auto">
				<v-btn
					small
					color="primary"
					@click="dialog = true"
				>
					<v-icon class="me-1">$add</v-icon>
					{{$T('add')}}
				</v-btn>
			</v-col>
		</v-row>

		<!-- table -->
		<bee-handy-table
			:headers="headers"
			:items="!loading ? fiscalYears : []"
			:items-per-page="-1"
			:loading="loading"
			:data-table-props="{ disableSort: true }"
			hide-default-footer
			dense
			shown-rows-text
			hide-shown-rows-text="auto"
		>
			<template v-if="permissions.fiscalYears.isReadWrite" v-slot:item.actions="{ item }">
				<div class="d-flex align-center">
					<v-switch
						v-model="item.isActive"
						readonly
						dense
						class="ma-0"
						hide-details
						@click="itemId = item.id; changeStateDialog = true;"
					/>

					<v-btn
						width="34"
						height="34"
						icon
						color="warning-color"
						class="ms-1"
						@click="itemId = item.id; dialog = true;"
					>
						<v-icon>$edit</v-icon>
					</v-btn>

					<v-btn
						width="34"
						height="34"
						icon
						color="alert-color"
						class="ms-1"
						@click="deleteItemId = item.id; deleteDialog = true;"
					>
						<v-icon>$delete</v-icon>
					</v-btn>
				</div>
			</template>
		</bee-handy-table>
	</div>
</template>

<script>
import rules from '@/helpers/validation-rules';
import { mapState, mapGetters } from 'vuex'
import moment from 'moment';
export default {
	name: 'FiscalYears',

	data() {
		return {
			rules,

			loading: false,
			submitLoading: false,
			changeStateLoading: false,
			
			dialog: false,
			changeStateDialog: false,
			deleteDialog: false,

			itemId: null,
			deleteItemId: null,

			// data
			name: null,
			startDate: null,
			endDate: null,
		}
	},

	watch: {
		dialog(val) {
			if (val) {
				if (this.itemId) {
					const fiscalYear = this.getFiscalYearById(this.itemId);
					this.name = fiscalYear.companyName;
					this.startDate = moment(fiscalYear.startDate).format('YYYY-MM-DD');
					this.endDate = moment(fiscalYear.endDate).format('YYYY-MM-DD');
				}
			} else {
				this.$refs.form.reset();
				this.itemId = null;
			}
		},

		changeStateDialog(val) {
			if (!val) {
				this.changeStateDialog = false;
			}
		}
	},

	computed: {
		...mapState({
			permissions: state => state.auth.self.permissions,
			fiscalYears: state => state.fiscalYears.fiscalYears	
		}),

		...mapGetters({
			getFiscalYearById: 'fiscalYears/getFiscalYearById'
		}),

		headers() {
			return [
				{
					text: this.$T('headers.name'),
					name: 'companyName',
					value: 'companyName'
				},
				{
					text: this.$T('headers.start-date'),
					name: 'startDate',
					value: 'startDate',
					type: 'date'
				},
				{
					text: this.$T('headers.end-date'),
					name: 'endDate',
					value: 'endDate',
					type: 'date'
				},
				{
					text: '',
					name: 'actions',
					value: 'actions',
					noNotAvailableText: true,
					width: 40
				},
			]
		}
	},

	methods: {
		fetchFiscalYears() {
			this.loading = true;
			this.$store.dispatch('fiscalYears/fetchAll').finally(() => {
				this.loading = false;
			})
		},

		submit() {
			if (this.$refs.form.validate()) {
				this.submitLoading = true;
				const actionName = !this.itemId ? 'create' : 'update';
				this.$store.dispatch(`fiscalYears/${actionName}`, {
					id: this.itemId,
					name: this.name,
					startDate: this.startDate,
					endDate: this.endDate
				}).then(() => {
					this.$eventBus.$emit('show-snackbar',
						!this.itemId
							? this.$T('messages.fiscal-year-added')
							: this.$T('messages.fiscal-year-edited') 
					);
					this.fetchFiscalYears();
					this.dialog = false;
				}).finally(() => {
					this.submitLoading = false;
				})
			}
		},

		changeActiveState() {
			this.changeStateLoading = true;
			this.$store.dispatch('fiscalYears/updateActiveState', {
				id: this.itemId
			}).then(() => {
				this.$eventBus.$emit('show-snackbar', this.$T('messages.fiscal-year-active-updated'));
				this.fetchFiscalYears();
				this.changeStateDialog = false;
				this.itemId = null;
			}).finally(() => {
				this.changeStateLoading = false;
			})
		}
	},

	created() {
		this.fetchFiscalYears();
	}
}
</script>

<style>

</style>